import type { CSSProperties } from "react";
import { Link } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PRODUCT_GROUPS, allProductsPath, productPath, productsByGroup } from "@/lib/products";

interface Props {
  /** Ortsname für Stadt-/Regionsseiten, ändert Titel und Link-Text. */
  city?: string;
  className?: string;
}

/**
 * REGEL (Produktübersicht-Block): Kompakte Übersicht ALLER Produkte nach Gruppe
 * mit Link auf /produkte. Einzige Produktliste auf Stadt-, Regions- und
 * Landingpages; nie zusätzlich ProductTeasers auf derselben Seite.
 * Der Knopf ist ein sekundärer Link, kein primärer Anfrage-CTA.
 * REGEL (keine Fotos): Hier NIE Produktfotos/Thumbnails zeigen (Auftrag Mark,
 * 05.10.2026) — nur Name + kleiner Akzent-Punkt in der Produktfarbe.
 * Erinnerungs-Stationen bleiben bewusst dezenter als die Hauptprodukte.
 */
const ProductOverviewBlock = ({ city, className = "" }: Props) => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const title = city ? t("productCatalog.block.titleCity", { city }) : t("productCatalog.block.title");
  const button = city ? t("productCatalog.block.buttonCity", { city }) : t("productCatalog.block.button");

  return (
    <section className={`py-12 sm:py-16 ${className}`} data-product-overview aria-labelledby="product-overview-h">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="rounded-3xl border border-border bg-card p-5 sm:p-8">
          <h2 id="product-overview-h" className="font-heading text-2xl font-bold text-foreground sm:text-3xl">
            {title}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground sm:text-base">{t("productCatalog.block.subline")}</p>
          <div className="mt-8 grid grid-cols-1 gap-7 md:grid-cols-2 lg:grid-cols-4">
            {PRODUCT_GROUPS.map((g) => {
              // REGEL: Stationen sind Zusatzprodukte und bleiben optisch
              // hinter den Hauptprodukten zurück (kein Akzent-Punkt, ruhiger).
              const isStation = g === "station";
              return (
                <div key={g}>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t(`productCatalog.groups.${g}.name`)}
                  </h3>
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {productsByGroup(g).map((p) => (
                      <li key={p.slug}>
                        <Link
                          to={productPath(p, isEn)}
                          style={{ ["--prod" as string]: p.accent } as CSSProperties}
                          className={
                            isStation
                              ? "inline-flex items-center rounded-full border border-border bg-card px-3.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground hover:[border-color:hsl(var(--prod)/0.6)]"
                              : "inline-flex items-center gap-2 rounded-full border border-border bg-muted/40 px-4 py-2 text-sm font-medium text-foreground transition-colors hover:[border-color:hsl(var(--prod)/0.6)] hover:[background:hsl(var(--prod)/0.08)]"
                          }
                        >
                          {!isStation && (
                            <span
                              aria-hidden="true"
                              className="h-2 w-2 shrink-0 rounded-full [background:hsl(var(--prod))]"
                            />
                          )}
                          {t(`productCatalog.items.${p.slug}.name`)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          <div className="mt-6">
            <Button asChild variant="outline">
              <Link to={allProductsPath(isEn)} data-all-products-link>
                {button}
                <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductOverviewBlock;
